/**
 * WindowSpecEditor — the structured PARTITION BY / ORDER BY / frame editor that
 * replaces the opaque OVER `<textarea>` in the full-mode window editor (Task
 * §4b-i, closes ANA-05/07/09/14/16/18/20/22). Controlled: it emits the next
 * `OverBodySpec` and the parent (`WindowOverEditor`) serializes it back to a
 * whole `OVER (…)` clause and commits via the existing `modifySelectWindow` op —
 * no new edit op, and the resolver/intellisense layer is untouched.
 *
 * Composed from three ≤200-line sub-pickers (`WindowPartitionPicker`,
 * `WindowOrderPicker`, `WindowFramePicker`) that mirror the existing chip /
 * ORDER-BY-row / dropdown idioms. Column suggestions are schema-driven via
 * `useColumnSuggestions` (the same source the SELECT/ORDER-BY pickers use).
 *
 * Designed to be reusable for a named-window body (§4b-ii): all window-specific
 * commit wiring lives in the parent, not here.
 */
import { useMemo } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { useColumnSuggestions } from '../../edit/useSchemaSuggestions';
import { vqeCopy } from '../../ux/vqeCopy';
import type { CommandPickerItem } from '../common/CommandPickerPopover';
import type { OverBodySpec } from './overBodySpec';
import type { ParsedOrderItem } from './parseSelectItem';
import { WindowFramePicker } from './WindowFramePicker';
import { WindowOrderPicker } from './WindowOrderPicker';
import { WindowPartitionPicker } from './WindowPartitionPicker';

/** Engines whose ORDER BY supports NULLS FIRST/LAST (mirrors `OrderBySection`). */
const NULLS_SUPPORTED: ReadonlySet<SqlEngine> = new Set<SqlEngine>(['postgres', 'oracle', 'sqlite']);

export interface WindowSpecEditorProps {
  readonly spec: OverBodySpec;
  readonly onChange: (next: OverBodySpec) => void;
  readonly editContext: VqeEditContext;
  readonly testIdPrefix: string;
  readonly engine: SqlEngine;
}

export function WindowSpecEditor({ spec, onChange, editContext, testIdPrefix, engine }: WindowSpecEditorProps) {
  const { track } = useAnalytics();
  const { columns, isLoading } = useColumnSuggestions({
    service: editContext.service,
    scope: editContext.scope,
    cacheKey: `${editContext.documentId}::${editContext.statementId}::${testIdPrefix}`,
  });

  const { columnItems, exprByKey } = useMemo(() => {
    const items: CommandPickerItem[] = [];
    const byKey = new Map<string, string>();
    for (const col of columns) {
      const key = `${col.relation.foldedName}.${col.qualified.foldedName}`;
      if (byKey.has(key)) continue;
      byKey.set(key, col.qualified.name);
      items.push({ key, label: col.qualified.name });
    }
    return { columnItems: items, exprByKey: byKey };
  }, [columns]);

  const resolveExpr = (key: string): string | undefined => exprByKey.get(key);

  const emit = (next: OverBodySpec, part: 'partition' | 'order' | 'frame'): void => {
    onChange(next);
    track('feature:vqe_window_edit', { part, engine });
  };

  const onPartitionChange = (partitionBy: readonly string[]): void =>
    emit({ ...spec, partitionBy }, 'partition');
  const onOrderChange = (orderBy: readonly ParsedOrderItem[]): void =>
    emit({ ...spec, orderBy }, 'order');
  const onFrameChange = (frame: OverBodySpec['frame']): void =>
    emit({ ...spec, frame, frameExclusion: frame ? spec.frameExclusion : null }, 'frame');

  return (
    <div className="flex flex-col gap-2" data-testid={`${testIdPrefix}-spec-editor`}>
      <WindowPartitionPicker
        partitionBy={spec.partitionBy}
        onChange={onPartitionChange}
        testIdPrefix={testIdPrefix}
        columnItems={columnItems}
        resolveExpr={resolveExpr}
        loading={isLoading}
      />
      <WindowOrderPicker
        orderBy={spec.orderBy}
        onChange={onOrderChange}
        testIdPrefix={testIdPrefix}
        showNulls={NULLS_SUPPORTED.has(engine)}
        columnItems={columnItems}
        resolveExpr={resolveExpr}
        loading={isLoading}
      />
      <WindowFramePicker
        frame={spec.frame}
        onChange={onFrameChange}
        testIdPrefix={testIdPrefix}
        engine={engine}
        orderByKeyCount={spec.orderBy.length}
      />
      {spec.frameExclusion ? (
        <div className="flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-frame-exclude`}>
          <span
            className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground"
            data-testid={`${testIdPrefix}-frame-exclude-chip`}
          >
            {spec.frameExclusion}
          </span>
          <span
            className="text-[10px] italic text-muted-foreground"
            data-testid={`${testIdPrefix}-frame-exclude-note`}
          >
            {vqeCopy.window.frameExcludeReadOnly}
          </span>
        </div>
      ) : null}
    </div>
  );
}
